import React, { useMemo, useState } from 'react';
import { Col, getItemWithExpiration, isZeroOrDefined, Row } from '../../lib/utils';
import { BoldText, DemiLightText, LightText, MediumText, RegularText } from '../../lib/Text';
import dayjs from 'dayjs';
import SvgIcon from '../../components/SvgIcon';
import { colors } from '../../lib/colors';
import FeedCenterStatisticsFeedWritingPercentIcon from './Icon/FeedCenterStatisticsFeedWritingPercentIcon';
import FeedCenterStatisticsReregister from './Icon/FeedCenterStatisticsReregister';
import useSingleQuery from '../../hook/useSingleQuery';
import { fetchGetFeedDashboard, GetFeedDashboardResponse } from '../../services/useFeedDashboardQueryService';
import { TooltipWrapper } from '../../components/TooltipWrapper';
import RateChangeText from './RateChangeText';
import { useRecoilState } from 'recoil';
import { recoilCenterReportMonth } from '@/atoms/atom';
import { TooltipIcon } from '../Settlement/SettlementCalendar';
import FeedCenterStatisticsPageGraph from './FeedCenterStatisticsPageGraph';
import FeedCenterStatisticsRanking from './FeedCenterStatisticsRanking';
import GroupClassToggle from '../../components/GroupClassToggle';
import FeedCenterStatisticsReregisterSuccessIcon from './Icon/FeedCenterStatisticsReregisterSuccessIcon';
import ReRegistrationSuccessEvidenceModal from './ReRegistrationSuccessEvidenceModal';

function FeedCenterStatisticsPage() {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const [authorization] = useState(getItemWithExpiration('authorization'));

    const [month, setMonth] = useRecoilState(recoilCenterReportMonth);
    const startDate = dayjs(month).startOf('month').format('YYYY.MM.DD');
    // 이번달일경우 오늘이 endDate
    const endDate = dayjs(month).isSame(dayjs(), 'month')
        ? dayjs().format('YYYY.MM.DD')
        : dayjs(month).endOf('month').format('YYYY.MM.DD');

    const { apiStartDate, apiEndDate } = useMemo(() => {
        const start = dayjs(month).startOf('month').toISOString();
        const end = dayjs(month).isSame(dayjs(), 'month')
            ? dayjs().toISOString()
            : dayjs(month).endOf('month').toISOString();
        return {
            apiStartDate: start,
            apiEndDate: end,
        };
    }, [month]);

    const { refinedResult, isLoading, isFetching } = useSingleQuery({
        fetchFn: fetchGetFeedDashboard,
        params: {
            centerId: authObject.centerId,
            startDate: apiStartDate,
            endDate: apiEndDate,
        },
        enabled: !!authorization,
        suspense: false,
    });
    const data = refinedResult?.data as GetFeedDashboardResponse;
    const centerAverageMetric = data?.centerAverageMetric;
    const monthlyMetricChanges = data?.monthlyMetricChanges;

    /** 센터 일지 작성률 */
    const centerReportWriteRate = centerAverageMetric?.centerReportWriteRate;
    /** 센터 일지 작성률 전월 대비 증감률 */
    const centerReportWriteRateChange = centerAverageMetric?.centerReportWriteRateChange;
    /** 센터 일지 수 */
    const centerReportCount = centerAverageMetric?.centerReportCount;
    /** 센터 수업 수 */
    const centerScheduleCount = centerAverageMetric?.centerScheduleCount;

    /** 센터 연속등록률 */
    const centerReRegistrationRate = centerAverageMetric?.centerReRegistrationRate || 0;
    /** 센터 연속등록률 전월 대비 증감률 */
    const centerReRegistrationRateChange = centerAverageMetric?.centerReRegistrationRateChange || 0;

    /** 센터 연속등록한 회원 수 */
    const centerReRegisteredMemberCount = centerAverageMetric?.centerReRegisteredMemberCount;
    const centerActiveMemberCount = centerAverageMetric?.centerActiveMemberCount;

    /** 종료월 기준 재등록 성공률 */
    const centerReRegistrationSuccessRate = centerAverageMetric?.centerReRegistrationSuccessRate;
    const centerReRegistrationSucceededMemberCount =
        centerAverageMetric?.centerReRegistrationSucceededMemberCount;
    const centerReRegistrationEndedMemberCount = centerAverageMetric?.centerReRegistrationEndedMemberCount;
    const centerReRegistrationNotYetMemberCount = centerAverageMetric?.centerReRegistrationNotYetMemberCount;
    const reRegistrationSuccessAsOf = centerAverageMetric?.reRegistrationSuccessAsOf;
    const reRegistrationSuccessDataQuality = centerAverageMetric?.reRegistrationSuccessDataQuality;

    const onNavigate = (direction: 'PREV' | 'NEXT') => {
        const currentMonth = dayjs(month);
        if (direction === 'PREV') {
            setMonth(currentMonth.subtract(1, 'month').toISOString());
        } else if (direction === 'NEXT') {
            // 미래달 안되게
            if (currentMonth.isSameOrAfter(dayjs(), 'month')) {
                return;
            }
            setMonth(currentMonth.add(1, 'month').toISOString());
        }
    };

    const [hoveredTooltip, setHoveredTooltip] = useState<'MEMBER_SHARE' | 'SUCCESS_RATE' | null>(null);
    const [isSuccessEvidenceVisible, setIsSuccessEvidenceVisible] = useState(false);

    const reRegistrationSuccessAsOfText = reRegistrationSuccessAsOf
        ? dayjs(reRegistrationSuccessAsOf).format('YYYY.MM.DD HH:mm')
        : null;
    const reRegistrationSuccessDataQualityText =
        reRegistrationSuccessDataQuality === 'ESTIMATED'
            ? '과거 데이터 추정치'
            : reRegistrationSuccessDataQuality === 'MIXED'
            ? '실시간·과거 데이터 혼합'
            : reRegistrationSuccessDataQuality === 'EXACT'
            ? '실시간 집계'
            : null;

    return (
        <div className="flex flex-col w-full">
            <Row className="flex items-center justify-between mb-[24px]">
                <Row className="flex items-center">
                    <MediumText fontSize={22} lineHeight={27} color={colors.Text_1} className="mr-[11px]">
                        {dayjs(month).format('YYYY년 MM월')}
                    </MediumText>
                <div
                    className="flex cursor-pointer bg-white w-[30px] h-[30px] justify-center items-center rounded-[4px] mr-[5px] border border-solid border-[#E0E0E0]"
                    onClick={() => onNavigate('PREV')}
                >
                    <SvgIcon
                        name={'SvgLeftStrokeArrow'}
                        size={15}
                        fill={colors.trans}
                        stroke={'#666B73'}
                        strokeWidth={1.75}
                    />
                </div>

                <div
                    className={`flex cursor-pointer bg-white w-[30px] h-[30px] justify-center items-center rounded-[4px] mr-[12px] border border-solid border-[#E0E0E0] 
                        ${dayjs(month).isSameOrAfter(dayjs(), 'month') ? ' opacity-30' : ' '}
                        `}
                    onClick={() => onNavigate('NEXT')}
                >
                    <SvgIcon
                        name={'SvgRightStrokeArrow'}
                        size={15}
                        fill={colors.trans}
                        stroke={'#666B73'}
                        strokeWidth={1.75}
                    />
                </div>
                <DemiLightText fontSize={16} lineHeight={21} color={colors.Text_1} className="mr-[40px]">
                    {startDate} - {endDate}
                </DemiLightText>
                <Col className="mr-[8px]">
                    <SvgIcon
                        name={'SvgFillUpArrow'}
                        size={7}
                        fill={colors.Text_1}
                        stroke={'#70747C'}
                        strokeWidth={1.2}
                    />
                    <SvgIcon
                        name={'SvgFillDownArrow'}
                        size={7}
                        fill={colors.Text_1}
                        stroke={'#70747C'}
                        strokeWidth={1.2}
                    />
                </Col>
                    <DemiLightText fontSize={16} lineHeight={21} color={colors.Text_1} className="mr-[40px]">
                        전월 대비 증감률
                    </DemiLightText>
                </Row>
                <GroupClassToggle />
            </Row>
            {/* 일지 작성률, 재등록 회원 비중, 재등록 성공률 */}
            <div className="grid grid-cols-3 gap-[20px] w-full mb-[40px]">
                <Row className="flex w-full bg-white rounded-[13px] h-[100px] justify-between items-center pl-[26px] pr-[30px]">
                    <TooltipWrapper
                        className="flex w-full"
                        tooltip={{
                            customContent: (
                                <Col className="flex w-full h-full items-center justify-center">
                                    <LightText fontSize={11} lineHeight={16} color={'white'} className="mb-[4px]">
                                        일지 수 / 수업수
                                    </LightText>
                                    <Col className="flex w-full h-[1px] bg-[#989B9F] mb-[4px]" />
                                    <DemiLightText fontSize={15} lineHeight={20} color={'white'}>
                                        {centerReportCount} / {centerScheduleCount}
                                    </DemiLightText>
                                </Col>
                            ),
                            px: 12,
                            py: 8,
                        }}
                    >
                        <Row className=" items-center justify-center">
                            <FeedCenterStatisticsFeedWritingPercentIcon />
                            <Row className="items-end mr-[27px]">
                                <BoldText fontSize={32} lineHeight={37} color={colors.Text_1} className="mr-[4px]">
                                    {isZeroOrDefined(centerReportWriteRate) ? centerReportWriteRate : '-'}
                                </BoldText>
                                <BoldText fontSize={17} lineHeight={32} color={colors.Text_1} className="">
                                    {isZeroOrDefined(centerReportWriteRate) ? '%' : ''}
                                </BoldText>
                            </Row>
                            <RegularText fontSize={14} lineHeight={18} color={'#6D7683'} className="">
                                일지 작성률
                            </RegularText>
                        </Row>
                    </TooltipWrapper>
                    <Row className=" items-center ">
                        <RateChangeText value={centerReportWriteRateChange || 0} isPercentage />
                    </Row>
                </Row>

                <Row className="flex w-full bg-white rounded-[13px] h-[100px] justify-between items-center pl-[26px] pr-[30px]">
                    <TooltipWrapper
                        tooltip={{
                            customContent: (
                                <Col className="flex w-full h-full items-center justify-center">
                                    <LightText fontSize={11} lineHeight={16} color={'white'} className="mb-[4px]">
                                        재등록 / 총 회원
                                    </LightText>
                                    <Col className="flex w-full h-[1px] bg-[#989B9F] mb-[4px]" />
                                    <DemiLightText fontSize={15} lineHeight={20} color={'white'}>
                                        {centerReRegisteredMemberCount} / {centerActiveMemberCount}
                                    </DemiLightText>
                                </Col>
                            ),
                            px: 12,
                            py: 8,
                        }}
                        disabled={hoveredTooltip === 'MEMBER_SHARE'}
                    >
                        <Row className="flex items-center justify-center">
                            <FeedCenterStatisticsReregister />
                            <Row className="flex items-end">
                                <BoldText fontSize={32} lineHeight={37} color={colors.Text_1} className="mr-[4px]">
                                    {isZeroOrDefined(centerReRegistrationRate) ? centerReRegistrationRate : '-'}
                                </BoldText>
                                <BoldText fontSize={17} lineHeight={32} color={colors.Text_1} className="mr-[10px]">
                                    {isZeroOrDefined(centerReRegistrationRate) ? '%' : ''}
                                </BoldText>
                            </Row>
                            <RegularText fontSize={14} lineHeight={18} color={'#6D7683'} className="">
                                재등록 회원 비중
                            </RegularText>
                            <TooltipIcon
                                title="* 해당 월 활성 수강권 중 재등록 수강권을 가진 회원의 비율입니다."
                                width={310}
                                letterSpacing={-0.5}
                                ml={6}
                                onMouseEnter={() => setHoveredTooltip('MEMBER_SHARE')}
                                onMouseLeave={() => setHoveredTooltip(null)}
                            />
                        </Row>
                    </TooltipWrapper>
                    <Row className="flex items-center ">
                        <RateChangeText value={centerReRegistrationRateChange || 0} isPercentage />
                    </Row>
                </Row>

                <Row className="flex w-full bg-white rounded-[13px] h-[100px] justify-between items-center pl-[22px] pr-[18px]">
                    <TooltipWrapper
                        tooltip={{
                            customContent: (
                                <Col className="flex w-full h-full items-center justify-center">
                                    <LightText fontSize={11} lineHeight={16} color={'white'} className="mb-[4px]">
                                        재등록 완료 / 정상 종료
                                    </LightText>
                                    <Col className="flex w-full h-[1px] bg-[#989B9F] mb-[4px]" />
                                    <DemiLightText fontSize={15} lineHeight={20} color={'white'}>
                                        {centerReRegistrationSucceededMemberCount ?? '-'} /{' '}
                                        {centerReRegistrationEndedMemberCount ?? '-'}
                                    </DemiLightText>
                                    <LightText fontSize={11} lineHeight={16} color={'#D7D9DC'} className="mt-[4px]">
                                        아직 미재등록 {centerReRegistrationNotYetMemberCount ?? '-'}명
                                    </LightText>
                                    {(reRegistrationSuccessAsOfText || reRegistrationSuccessDataQualityText) && (
                                        <LightText fontSize={10} lineHeight={15} color={'#BFC2C6'} className="mt-[2px]">
                                            {reRegistrationSuccessAsOfText
                                                ? `${reRegistrationSuccessAsOfText} 기준`
                                                : ''}
                                            {reRegistrationSuccessAsOfText && reRegistrationSuccessDataQualityText
                                                ? ' · '
                                                : ''}
                                            {reRegistrationSuccessDataQualityText}
                                        </LightText>
                                    )}
                                </Col>
                            ),
                            width: 280,
                            px: 12,
                            py: 8,
                        }}
                        disabled={hoveredTooltip === 'SUCCESS_RATE'}
                    >
                        <Row className="flex items-center justify-center">
                            <FeedCenterStatisticsReregisterSuccessIcon />
                            <Row className="flex items-end">
                                <BoldText fontSize={32} lineHeight={37} color={colors.Text_1} className="mr-[4px]">
                                    {isZeroOrDefined(centerReRegistrationSuccessRate)
                                        ? centerReRegistrationSuccessRate
                                        : '-'}
                                </BoldText>
                                <BoldText fontSize={17} lineHeight={32} color={colors.Text_1} className="mr-[10px]">
                                    {isZeroOrDefined(centerReRegistrationSuccessRate) ? '%' : ''}
                                </BoldText>
                            </Row>
                            <RegularText fontSize={14} lineHeight={18} color={'#6D7683'}>
                                재등록 성공률
                            </RegularText>
                            <TooltipIcon
                                title="* 해당 월에 PT를 정상 종료한 회원 중 조회 시점까지 재등록한 회원의 비율입니다. 인정기간 제한이 없어 이후 재등록 시 과거 수치가 변경될 수 있습니다."
                                width={390}
                                letterSpacing={-0.5}
                                ml={6}
                                onMouseEnter={() => setHoveredTooltip('SUCCESS_RATE')}
                                onMouseLeave={() => setHoveredTooltip(null)}
                            />
                        </Row>
                    </TooltipWrapper>
                    <button
                        type="button"
                        onClick={() => setIsSuccessEvidenceVisible(true)}
                        className="ml-[8px] flex shrink-0 items-center gap-[3px] rounded-[7px] border border-[#CFE9E1] bg-[#F3FAF8] px-[8px] py-[6px] text-[11px] font-medium text-[#167A5F] hover:bg-[#E9F7F2]"
                    >
                        근거 보기
                        <SvgIcon
                            name="SvgRightStrokeArrow"
                            size={10}
                            stroke="#167A5F"
                            fill="transparent"
                            strokeWidth={1.7}
                        />
                    </button>
                </Row>
            </div>
            {/* 지표별 강사 순위 (전체 너비) */}
            <div className="w-full pb-[24px]">
                <FeedCenterStatisticsRanking centerId={authObject.centerId} />
            </div>
            {/* 최근 4개월 지표 변화 */}
            <div className="w-full pb-[32px]">
                <FeedCenterStatisticsPageGraph monthlyMetricChanges={monthlyMetricChanges} month={month} />
            </div>
            {isSuccessEvidenceVisible && (
                <ReRegistrationSuccessEvidenceModal
                    centerId={authObject.centerId}
                    cohortMonth={dayjs(month).format('YYYY-MM')}
                    successRate={centerReRegistrationSuccessRate}
                    succeededMemberCount={centerReRegistrationSucceededMemberCount}
                    endedMemberCount={centerReRegistrationEndedMemberCount}
                    notYetMemberCount={centerReRegistrationNotYetMemberCount}
                    asOf={reRegistrationSuccessAsOf}
                    dataQuality={reRegistrationSuccessDataQuality}
                    onClose={() => setIsSuccessEvidenceVisible(false)}
                />
            )}
        </div>
    );
}

export default FeedCenterStatisticsPage;
